import { createContext } from 'react'
import { isAxiosError } from 'axios'
import { HTTPInstance } from '@/lib/http/http-instance'
import type { DeliveryConnectionsClient, DeliveryProvider } from './types'

// Explicit restaurant ids captured by the screen: no interceptor reading a
// possibly changed localStorage value between rendering and submitting.
const path = (restaurantId: string, provider: DeliveryProvider) =>
  `/restaurants/${encodeURIComponent(restaurantId)}/integrations/${provider}`

export const deliveryConnectionsAPI: DeliveryConnectionsClient = {
  get: (id, provider, signal) =>
    HTTPInstance.get(path(id, provider), { signal }).then(r => r.data),
  save: (id, provider, connection) =>
    HTTPInstance.put(path(id, provider), {
      externalStoreId: connection.externalStoreId,
      enabled: connection.enabled,
      ...(provider === 'uber-eats'
        ? { acceptanceMode: connection.acceptanceMode }
        : {})
    }).then(r => r.data),
  // Only the mode: the full PUT would carry this screen's copy of `enabled`
  // back to the server, and a copy that went stale while the panel was open
  // would switch order routing with it.
  setAcceptanceMode: (id, acceptanceMode) =>
    HTTPInstance.patch(`${path(id, 'uber-eats')}/acceptance-mode`, {
      acceptanceMode
    }).then(r => r.data),
  capabilities: id =>
    HTTPInstance.get(`${path(id, 'uber-eats')}/capabilities`).then(r => r.data),
  start: id =>
    HTTPInstance.post(`${path(id, 'uber-eats')}/connect`).then(r => r.data),
  cancel: (id, sessionId) =>
    HTTPInstance.delete(
      `${path(id, 'uber-eats')}/connect/${encodeURIComponent(sessionId)}`
    ).then(() => undefined).catch((error: unknown) => {
      if (isAxiosError(error) && error.response?.status === 404) return
      throw error
    }),
  session: (id, sessionId, signal) =>
    HTTPInstance.get(
      `${path(id, 'uber-eats')}/connect/${encodeURIComponent(sessionId)}`,
      { signal }
    ).then(r => r.data),
  activate: (id, sessionId, storeId, acceptanceMode) =>
    HTTPInstance.post(
      `${path(id, 'uber-eats')}/connect/${encodeURIComponent(sessionId)}/activate`,
      { storeId, acceptanceMode }
    ).then(r => r.data),
  verify: id =>
    HTTPInstance.post(`${path(id, 'uber-eats')}/verify`).then(r => r.data),
  disconnect: id =>
    HTTPInstance.delete(`${path(id, 'uber-eats')}/connection`).then(
      () => undefined
    )
}

export const DeliveryConnectionsContext =
  createContext<DeliveryConnectionsClient>(deliveryConnectionsAPI)
